/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/require-computed-property-dependencies, getter-return, no-prototype-builtins */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { isArray } from '@ember/array';
import EmberObject, { get, set, action, computed } from '@ember/object';
import { sum, mapBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cleanFileName } from '@linc-technologies/linced-common/utils/file-names';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import {
  getMultiTablesExportData,
  printMultiTables,
} from 'linced-app/utils/table';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import moment from 'moment';
import config from 'linced-app/config/environment';
import { getErrorMessages } from 'linced-app/utils/xero-errors';
import {
  byKeys,
  filterBy,
  findBy,
  mapBy as mapByArray,
} from '@linc-technologies/linced-common/utils/arrays';

function getLedgerName(transaction) {
  let ledger = transaction.get('lineItem.ledger');
  let ledgerName = '';
  if (ledger) {
    ledgerName = `${ledger.get('code')} - ${ledger.get('name')}`;
  }
  return ledgerName;
}

export default class ReconciliationController extends Controller {
  // Services
  @service
  csv;

  @service
  excel;

  @service
  features;

  @service
  fetch;

  @service
  finance;

  @service
  options;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  PaymentMethod = PaymentMethod;
  TransactionType = TransactionType;

  @alias('session.school.name')
  schoolName;

  @optionAlias('finance:xero:enableXeroIntegration')
  xeroIntegrationEnabled;

  @optionAlias('finance:xero:heroClearingAccountCode')
  heroClearingAccountCode;

  @optionAlias('finance:xero:heroJournalPostingType')
  heroJournalPostingType;

  @optionAlias('finance:xero:piaLedgerCode')
  piaLedgerCode;

  selectedLineItems = EmberObject.create(
    PaymentMethod.getAllValues().reduce((map, val) => {
      map[val] = [];
      return map;
    }, {}),
  );

  @tracked
  from = '';

  @tracked
  to = '';

  @tracked
  creatingBatch = false;

  @tracked
  markingBatchPosted = false;

  @tracked
  sortProp = 'date';

  @tracked
  sortDir = 'asc';

  // @tracked, not a plain field: mutation sites rebuild and reassign rather
  // than mutating an EmberArray in place, and this is read directly in the
  // template via {{includes method this.methodsWithAllSelected}}.
  @tracked
  methodsWithAllSelected = [];

  @tracked
  dateDialogOpen = false;

  @tracked
  selectedMethod = null;

  // @tracked, not a plain field: mutation sites (filter(), reset()) rebuild
  // and reassign rather than mutating an EmberArray in place - the classic
  // @computed('filters.@each...') getters below rely on that reassignment
  // to know to recompute, since filters is never itself read in a template.
  @tracked
  filters = [];

  @computed('filters.@each.{field,value}')
  get filterMap() {
    return this.filters.reduce((map, filter) => {
      map[filter.field] = filter.value;
      return map;
    }, {});
  }

  get reportTitle() {
    return `Bank Reconciliation report: ${this.from} - ${this.to}`;
  }

  @alias('finance.allPaymentMethods')
  paymentMethods;

  @computed('model.payments.@each.method', 'paymentMethods.@each.value')
  get displayMethods() {
    const valid = new Set(this.paymentMethods.map((item) => item.value));
    return [...new Set(this.model.payments.map((item) => item.method))].filter(
      (method) => valid.has(method),
    );
  }

  @computed('refundData.[]', 'paymentData.[]')
  get totals() {
    let data = [];
    PaymentMethod.values.forEach((method) => {
      let payments = get(this.paymentData, method.value);
      if (!isEmpty(payments)) {
        let total = payments.reduce(
          (total, payment) => total + this.getTransactionAmount(payment),
          0,
        );
        data.push({
          method: method.value,
          total,
        });
      }
    });
    if (!isEmpty(this.refundData)) {
      let total = this.refundData.reduce(
        (total, refund) => total + this.getTransactionAmount(refund),
        0,
      );
      data.push({
        isRefund: true,
        total,
      });
    }

    return data;
  }

  // @tracked, not a plain field: addToBatch/toggleSelectAll/resetSelection
  // rebuild and reassign rather than mutating an EmberArray in place, and
  // this is read directly in the template via
  // {{includes transaction.id this.selectedForCheck}}.
  @tracked
  selectedForCheck = [];

  @computed('filteredPaymentData.[]', 'filteredRefunds.[]')
  get filteredTotals() {
    let data = [];
    PaymentMethod.values.forEach((method) => {
      let payments = get(this.filteredPaymentData, method.value);
      if (!isEmpty(payments)) {
        // NOTE: These "payments" are table-ready rows, with the `amount` already determined.
        // Don't use `getTransactionAmount()`.
        let total = payments.reduce(
          (total, payment) => total + payment.amount,
          0,
        );
        data.push({
          method: method.value,
          total,
        });
      }
    });
    if (!isEmpty(this.filteredRefunds)) {
      let total = this.filteredRefunds.reduce(
        (total, refund) => total + this.getTransactionAmount(refund),
        0,
      );
      data.push({
        isRefund: true,
        total,
      });
    }

    return data;
  }

  @computed('paymentData.[]', 'refundData.[]')
  get totalsByLedger() {
    let totals = [];
    for (let paymentMethod in this.paymentData) {
      if (this.paymentData.hasOwnProperty(paymentMethod)) {
        let payments = get(this.paymentData, paymentMethod);
        payments.forEach((payment) => {
          let ledger = payment.ledger;
          let ledgerId = payment.ledgerId;
          if (ledgerId === 'PIA') {
            ledger = 'PIA';
          }
          let txnAmount = payment.amount;
          // Find or create the totals row for this Ledger (or PIA).
          let row = findBy(totals, 'ledger', ledgerId);
          if (isEmpty(row)) {
            totals.push({
              ledger: ledgerId,
              code: ledger,
              [paymentMethod]: txnAmount,
              total: txnAmount,
            });
          } else {
            // Add applicable amount to the payment method and total for this Ledger (or PIA).
            let amount = get(row, paymentMethod) ?? 0;
            amount += txnAmount;
            set(row, paymentMethod, amount);
            row.total += txnAmount;
          }
        });
      }
    }

    if (!isEmpty(this.refundData)) {
      totals = this.refundData.reduce((data, refund) => {
        let id = refund.ledgerId;
        let code = refund.ledgerId ? refund.ledger : 'PIA';
        let row = findBy(data, 'ledger', id);
        if (isEmpty(row)) {
          data.push({ ledger: id, code, total: 0 });
          row = findBy(data, 'ledger', id);
        }

        let amount = get(row, 'Refund') ?? 0;
        let refundAmount = this.getTransactionAmount(refund);
        amount += refundAmount;
        set(row, 'Refund', amount);
        row.total += refundAmount;

        return data;
      }, totals);
    }
    return totals
      .slice()
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''));
  }

  @mapBy('totals', 'total')
  totalValues;

  @sum('totalValues')
  grandTotal;

  getTransactionAmount(txn) {
    if (isEmpty(get(txn, 'lineItem.id'))) {
      // No line item - this is an account-related transaction (PIA) - use `amount`.
      return txn.amount;
    }
    return txn.displayAmount;
  }

  // transactionFilters maps the raw filters into what we need to actually perform the filtering.
  @computed('filters.@each.{field,value}')
  get transactionFilters() {
    return this.filters.map((filter) => {
      let dataType = DataType.STRING;
      let key = filter.field;
      switch (filter.field) {
        case 'item':
          key = 'item.name';
          break;
        case 'description':
          key = 'item.description';
          break;
        case 'firstName':
        case 'lastName':
          key = `account.person.${filter.field}`;
          break;
        case 'amount':
          dataType = DataType.FLOAT;
          break;
        case 'stripeId':
        case 'xeroBatchNo':
        case 'nswCppId':
          dataType = DataType.INTEGER;
          break;
      }

      return {
        key,
        dataType,
        value: filter.value,
      };
    });
  }

  @computed('filters.@each.{field,value}', 'paymentData.[]')
  get filteredPaymentData() {
    if (isEmpty(this.filters)) {
      return this.paymentData;
    }

    let filtered = {};
    for (let [key, value] of Object.entries(this.paymentData)) {
      filtered[key] = value.filter((row) => {
        return this.transactionFilters.every((filter) => {
          return filterValue(
            get(row, filter.key) ?? '',
            filter.dataType,
            filter.value,
          );
        });
      });
    }

    return filtered;
  }

  @computed('filters.@each.{field,value}', 'refundData.[]')
  get filteredRefunds() {
    if (isEmpty(this.filters) || isEmpty(this.refundData)) {
      return this.refundData;
    }
    // If xeroBatchNumber is used in method filters return empty refunds data.
    if (!isEmpty(filterBy(this.filters, 'field', 'xeroBatchNo'))) {
      return;
    }

    // Apply all filters to each row in one hit, to avoid iterating over a (potentially) large dataset multiple times.
    return this.refundData.filter((row) => {
      return this.transactionFilters.every((filter) => {
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  // paymentData is set of arrays of table-ready data rows, to ease the sorting and filtering process.
  @computed('model.payments.[]', 'filterFromBatchNo', 'filterToBatchNo')
  get paymentData() {
    let allPaymentData = this.model.payments
      .slice()
      .sort((a, b) => (a.id || '').localeCompare(b.id || ''))
      .reduce((data, transaction) => {
        let rows = get(data, transaction.method);
        if (!isArray(rows)) {
          data[transaction.method] = [];
          rows = get(data, transaction.method);
        }

        // Payment records may have apportionment (child transactions), which should be displayed in the table.
        transaction.childTransactions.forEach((txn) => {
          let item = txn.lineItem;
          let isXeroLedger = Boolean(txn.get('lineItem.ledger.taxType'));
          let ledgerId = txn.get('lineItem.ledger.id');
          if (isEmpty(item.get('id'))) {
            item = {
              name: 'Payment in Advance',
            };
            isXeroLedger = true;
            ledgerId = 'PIA';
          }

          rows.push({
            id: parseInt(txn.id, 10),
            ledger: getLedgerName(txn),
            date: transaction.date,
            item,
            account: txn.account,
            amount: this.getTransactionAmount(txn),
            stripeId: transaction.stripeTransactionId,
            nswCppId: transaction.nswCppTransactionId || '',
            email: transaction.get('person.fields.core:emailAddress.value'),
            xeroBatchNo: txn.xeroBatchNo,
            ledgerId,
            isPosted: txn.isPosted,
            isXeroLedger,
            paidBy: transaction.paidBy,
            chequeNo: transaction.chequeNo,
            chequeBSB: transaction.chequeBSB,
            chequeAccountNo: transaction.chequeAccountNo,
          });
        });

        if (transaction.piaAmount > 0) {
          rows.push({
            id: parseInt(transaction.id, 10),
            ledger: '',
            date: transaction.date,
            item: { name: 'Payment in Advance' },
            account: transaction.account,
            amount: transaction.piaAmount,
            stripeId: transaction.stripeTransactionId,
            nswCppId: transaction.nswCppTransactionId || '',
            email: transaction.get('person.fields.core:emailAddress.value'),
            xeroBatchNo: transaction.xeroBatchNo,
            ledgerId: 'PIA',
            isPosted: transaction.isPosted,
            isXeroLedger: true,
            paidBy: transaction.paidBy,
            chequeNo: transaction.chequeNo,
            chequeBSB: transaction.chequeBSB,
            chequeAccountNo: transaction.chequeAccountNo,
          });
        }

        return data;
      }, {});
    if (isEmpty(this.filterFromBatchNo) && isEmpty(this.filterToBatchNo)) {
      return allPaymentData;
    }
    let filtered = {};
    for (let [key, value] of Object.entries(allPaymentData)) {
      filtered[key] = value.filter((row) => {
        return (
          !isEmpty(row.xeroBatchNo) &&
          row.xeroBatchNo >= this.filterFromBatchNo &&
          row.xeroBatchNo <= this.filterToBatchNo
        );
      });
    }
    return filtered;
  }

  // refundData is set of arrays of table-ready data rows, to ease the sorting and filtering process.
  @computed('model.refunds.[]', 'filterToBatchNo')
  get refundData() {
    if (!isEmpty(this.filterToBatchNo) || isEmpty(this.model.refunds)) {
      return;
    }

    return this.model.refunds.reduce((rows, transaction) => {
      let item = transaction.lineItem;
      if (isEmpty(item.get('id'))) {
        item = {
          name: 'Payment in Advance',
        };
      }
      rows.push({
        ledgerId: transaction.get('lineItem.ledger.id') ?? 'PIA',
        ledger: getLedgerName(transaction),
        date: transaction.date,
        item,
        account: transaction.account,
        amount: this.getTransactionAmount(transaction),
        refundMethodLabel: transaction.refundMethodLabel,
      });

      return rows;
    }, []);
  }

  getTables() {
    return {
      titles: document.querySelectorAll('h3.bank-reconciliation-title'),
      tables: document.querySelectorAll(
        'md-table-container.bank-reconciliation-table',
      ),
    };
  }

  @tracked
  filterFromBatchNo = null;

  @tracked
  filterToBatchNo = null;

  @tracked
  fromBatchNo = null;

  @action
  setFromBatchNo(value) {
    this.fromBatchNo = value;
  }

  @tracked
  toBatchNo = null;

  @action
  setToBatchNo(value) {
    this.toBatchNo = value;
  }

  @computed('model.payments.[]')
  get latestXeroBatchNo() {
    return this.model.payments.reduce((batchNo, transaction) => {
      if (!isEmpty(transaction.xeroBatchNo)) {
        batchNo = Math.max(transaction.xeroBatchNo, batchNo);
      }
      return transaction.childTransactions
        .filter((item) => item.xeroBatchNo)
        .reduce((xeroBatchNo, txn) => {
          return Math.max(txn.xeroBatchNo, batchNo);
        }, batchNo);
    }, 0);
  }

  @tracked
  totalAmount = 0;

  @tracked
  totalTransactions = 0;

  @tracked
  errorDialogOpen = false;

  @tracked
  errorMessages = [];

  @tracked
  errorHeader = 'Failed to create a Batch';

  @tracked
  piaAmount;

  @tracked
  journalDate;

  @action
  setJournalDate(value) {
    this.journalDate = value;
  }

  @action
  getSelectedLineItems(method) {
    // Note: this is called in the template with a second param in order to
    // force the template to update when `selectedLineItems` is reset.
    return this.get(`selectedLineItems.${method}`);
  }

  @action
  toggleDateDialog(method) {
    if (!this.dateDialogOpen) {
      if (!isEmpty(this.selectedLineItems.get(method))) {
        let filteredPayments = get(this.paymentData, method).filter(
          (payment) => {
            return this.selectedLineItems.get(method).includes(payment.id);
          },
        );
        let piaAmount = filterBy(filteredPayments, 'ledgerId', 'PIA').reduce(
          (sum, payment) => sum - payment.amount,
          0,
        );
        let totalAmount = filteredPayments.reduce(
          (sum, payment) => sum + payment.amount,
          0,
        );
        let journalDate = filteredPayments.sort(byKeys('date')).at(-1)?.date;
        this.dateDialogOpen = true;
        this.piaAmount = piaAmount;
        this.journalDate = journalDate;
        this.selectedMethod = method;
        this.totalAmount = totalAmount;
        this.totalTransactions = filteredPayments.length;
      } else {
        this.toaster.error('Please select transactions to proceed.');
        this.creatingBatch = false;
      }
    } else {
      this.toggleProperty('dateDialogOpen');
    }
  }

  @action
  toggleSelectAll(method) {
    if (
      isEmpty(this.methodsWithAllSelected) ||
      !this.methodsWithAllSelected.includes(method)
    ) {
      this.methodsWithAllSelected = [...this.methodsWithAllSelected, method];
      // Select all filtered payment data for method except that already consists xero batch no.
      // Transactions with xeroBatchNo have already been sent to Xero.
      let filtered = this.filteredPaymentData[method].reduce(
        (ids, transaction) => {
          if (!transaction.isPosted && transaction.isXeroLedger) {
            ids.push(transaction.id);
          }
          return ids;
        },
        [],
      );
      set(this.selectedLineItems, method, [
        ...this.selectedLineItems.get(method),
        ...filtered,
      ]);
      this.selectedForCheck = [...this.selectedForCheck, ...filtered];
    } else {
      this.methodsWithAllSelected = this.methodsWithAllSelected.filter(
        (item) => item !== method,
      );
      let filtered = mapByArray(this.filteredPaymentData[method], 'id').filter(
        (id) => {
          return !isEmpty(id);
        },
      );
      set(this.selectedLineItems, method, []);
      this.selectedForCheck = this.selectedForCheck.filter(
        (id) => !filtered.includes(id),
      );
    }
  }

  @action
  markAsPosted(method) {
    if (isEmpty(this.selectedLineItems.get(method))) {
      this.toaster.error('Please select transactions to proceed.');
      return;
    }
    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/transactions/:bulkUpdateTxnXeroBatch`;
    let data = {
      schoolId: this.session.activeSchool,
      transactionIds: this.selectedLineItems.get(method),
      xeroBatchNo: 0,
      isPosted: true,
    };
    this.markingBatchPosted = true;
    this.selectedMethod = method;
    return this.fetch
      .post(url, { data })
      .then(() => {
        this.resetSelection();
        this.markingBatchPosted = false;
        this.router.refresh('admin.reports.finance.reconciliation');
        this.toaster.success(
          'Transactions has been marked as posted successfully!',
        );
      })
      .catch(() => {
        this.markingBatchPosted = false;
        this.toaster.error(
          'Error in marking transactions as posted. Please try again.',
        );
      });
  }

  @action
  closeErrorDialog() {
    this.errorDialogOpen = false;
  }

  @action
  sendBatchToXero(methodLabel) {
    let method = this.selectedMethod;
    this.creatingBatch = true;
    this.store
      .createRecord('xero/batch', {
        schoolId: this.session.school,
        transactionIds: this.selectedLineItems.get(method),
        heroClearingAccountCode: this.heroClearingAccountCode,
        batchStatus: this.heroJournalPostingType,
        piaLedgerCode: this.piaLedgerCode,
        piaAmount: this.piaAmount,
        narration: `Hero ${methodLabel.toUpperCase()} Batch %d - Sent ${moment().format(
          'DD/MM/YYYY',
        )}`,
        journalDate: moment(this.journalDate).format('YYYY-MM-DD'),
      })
      .save()
      .then((batchResponse) => {
        this.resetSelection();
        this.creatingBatch = false;
        this.dateDialogOpen = false;
        if (!isEmpty(batchResponse.warningMessages)) {
          this.errorMessages = batchResponse.warningMessages;
          this.errorHeader = `Batch Number ${batchResponse.batchNo} created with warnings`;
          this.errorDialogOpen = true;
        } else {
          this.toaster.success(
            `Batch Number ${batchResponse.batchNo} sent successfully!`,
          );
        }
        this.router.refresh('admin.reports.finance.reconciliation');
      })
      .catch((err) => {
        this.errorMessages = getErrorMessages(err);
        this.errorHeader = 'Failed to create Batch';
        this.errorDialogOpen = true;
        this.dateDialogOpen = false;
      })
      .finally(() => {
        this.creatingBatch = false;
      });
  }

  @action
  addToBatch(lineItem, method) {
    let itemId = lineItem;
    if (this.selectedLineItems.get(method).includes(itemId)) {
      set(
        this.selectedLineItems,
        method,
        this.selectedLineItems.get(method).filter((id) => id !== itemId),
      );
      this.selectedForCheck = this.selectedForCheck.filter(
        (id) => id !== itemId,
      );
    } else {
      set(this.selectedLineItems, method, [
        ...this.selectedLineItems.get(method),
        itemId,
      ]);
      this.selectedForCheck = [...this.selectedForCheck, itemId];
    }
  }

  @action
  onChangeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((item) => item !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  printAll() {
    let data = this.getTables();

    printMultiTables(this.reportTitle, data.titles, data.tables);
  }

  @action
  downloadAll() {
    let rawFileName = `${this.reportTitle}`;
    let fileName = cleanFileName(rawFileName) + '.xlsx';

    let data = this.getTables();
    let sheets = getMultiTablesExportData(data.titles, data.tables);

    return this.excel.export(sheets, { multiSheet: true, fileName });
  }

  @action
  searchByBatchNo(fromBatchNo, toBatchNo) {
    if (
      !(fromBatchNo || toBatchNo) ||
      isNaN(fromBatchNo) ||
      isNaN(toBatchNo) ||
      fromBatchNo < 0 ||
      toBatchNo < 0
    ) {
      this.toaster.error('Please enter valid input for batch numbers.');
      this.resetBatch();
      return;
    }
    if (isEmpty(this.latestXeroBatchNo)) {
      this.toaster.error(
        'Xero Batch number does not exist for selected date range.',
      );
      this.resetBatch();
      return;
    }
    fromBatchNo = fromBatchNo ? parseInt(fromBatchNo, 10) : 0;
    let latestXeroBatchNo = parseInt(this.latestXeroBatchNo, 10);
    if (fromBatchNo > latestXeroBatchNo) {
      this.toaster.error(
        `From batch no. should be less than the latest batch no. ${latestXeroBatchNo}.`,
      );
      this.resetBatch();
      return;
    }
    toBatchNo = toBatchNo ? parseInt(toBatchNo, 10) : latestXeroBatchNo;
    if (fromBatchNo > toBatchNo) {
      this.toaster.error('From batch no. should be less than To batch no.');
      this.resetBatch();
      return;
    }
    this.filterFromBatchNo = fromBatchNo;
    this.filterToBatchNo = toBatchNo;
    this.toBatchNo = toBatchNo;
    this.fromBatchNo = fromBatchNo;
  }

  @action
  resetBatchSearch() {
    this.resetBatch();
  }

  @action
  numColumns(paymentMethod) {
    let num = 7; // Base number of columns.
    if (this.xeroIntegrationEnabled) {
      // Xero adds a column for batch number.
      num++;
    }
    if (this.features.sapFinance) {
      switch (paymentMethod) {
        case PaymentMethod.CHEQUE:
          num += 3;
          break;
        case PaymentMethod.ONLINE:
          // NSW CPP adds one column.
          num += 1;
          break;
      }
    } else {
      switch (paymentMethod) {
        case PaymentMethod.ONLINE:
          // Stripe adds two columns.
          num += 2;
          break;
      }
    }

    return num;
  }

  reset() {
    this.filters = [];
    this.resetSelection();
    this.resetBatch();
  }

  resetSelection() {
    this.methodsWithAllSelected = [];
    this.selectedForCheck = [];
    PaymentMethod.getAllValues().forEach((method) => {
      set(this.selectedLineItems, method, []);
    });
  }

  resetBatch() {
    this.filterFromBatchNo = null;
    this.filterToBatchNo = null;
    this.fromBatchNo = null;
    this.toBatchNo = null;
  }
}
